import React, { useState, useEffect, useMemo } from 'react'
import { InputText, InputMasked } from '../../../ui'
import { LATIN_WITH_NUMBERS_SYMBOLS, MAX_LENGTH, getIbanMask } from '../../../constants/constants'
import { useTranslation } from 'react-i18next'
import { map } from 'ramda'
import { time as uniqid } from 'uniqid'
import { useStore } from '../../../StateProvider'
import { getUserValue } from '../utils'

/**
 *
 * @param {{
 *  label: string,
 *  name: string,
 *  maxlength?: number
 *  allowNonLatin: boolean
 * }} props
 */
const ExternalPurposeAccountDependentField = ({ label, name, maxlength, allowNonLatin }) => {
  const { t } = useTranslation()
  const [value, setValue] = useState('')
  const [isValid, setIsValid] = useState(false)
  const [isFilled, setIsFilled] = useState(false)
  const [isShown, setIsShown] = useState(name !== 'descriptionPurposeAccount')
  const [error, setError] = useState('')
  const [{ form, form: { accId }, user }, dispatch] = useStore()
  const { showPurposeAccountDescription } = form[accId]
  const userValue = getUserValue(accId, user, name)

  useEffect(() => {
    if (name === 'descriptionPurposeAccount') {
      setIsShown(showPurposeAccountDescription)
      if (showPurposeAccountDescription) {
        dispatch({ type: 'addValidationFld', name })
      } else {
        setValue('')
        setIsValid(false)
        setIsFilled(false)
        dispatch({ type: 'removeValidationFld', name })
        dispatch({ type: 'removeFieldValue', name })
      }
    }
  }, [showPurposeAccountDescription, name])

  const presetValue = (value) => {
    setValue(value)
    const isValid = !!value.length
    setIsValid(isValid)
    !value.trim() ? setIsFilled(false) : setIsFilled(true)
    dispatch({ type: 'setExtFieldValue', name, value })
    dispatch({ type: 'setValidationFld', name, value: isValid })
  }

  useEffect(() => {
    if ((name === 'descriptionPurposeAccount' && showPurposeAccountDescription &&
      userValue && userValue.length) || (name !== 'descriptionPurposeAccount' &&
      userValue && userValue.length)) {
      presetValue(userValue)
    }
  }, [userValue, showPurposeAccountDescription, name])

  const mask = useMemo(() => {
    if (name === 'iban') {
      return getIbanMask()
    }
  }, [name])

  const onChange = ({ target: { value } }) => {
    setValue(value)
    !value.trim() ? setIsFilled(false) : setIsFilled(true)
  }

  const onBlur = ({ currentTarget: { value, name } }) => {
    const trimmedValue = value.trim()
    const isValid = allowNonLatin ? !!trimmedValue.length : LATIN_WITH_NUMBERS_SYMBOLS.test(trimmedValue)

    dispatch({ type: 'setExtFieldValue', name, value })
    dispatch({ type: 'setValidationFld', name, value: isValid })
    setIsValid(isValid)
    setError(!isFilled || isValid ? '' : 'generatedField.onlyLatin')
  }

  /**
   *
   * @param name
   * @returns {number}
   */
  const getMaxLength = (name) => {
    switch (name) {
      case 'bankName':
      case 'bankAccountName':
      case 'bankAccountNumber':
        return 200
      default:
        return MAX_LENGTH
    }
  }

  if (name === 'iban') {
    return isShown ? (
      <InputMasked
        name={name}
        label={t(label)}
        value={value}
        mask={mask}
        guide={false}
        key={name}
        maxLength={maxlength || MAX_LENGTH}
        error={t(error)}
        onChange={onChange}
        onBlur={onBlur}
        valid={isValid}
      />
    ) : null
  } else {
    return isShown ? (
      <InputText
        value={value}
        label={t(label)}
        placeholder=''
        error={t(error)}
        name={name}
        onChange={onChange}
        onBlur={onBlur}
        maxLength={maxlength || getMaxLength(name)}
        valid={isValid}
      />
    ) : null
  }
}

const getExternalPurposeAccountDependentFields = map(field => {
  const { i18n, name, required, order, maxLength, allowNonLatin } = field

  return (
    <ExternalPurposeAccountDependentField
      label={i18n ? `generatedField.${i18n}` : ''}
      name={name}
      required={required}
      order={order}
      key={`${name}${uniqid()}`}
      maxlength={maxLength}
      allowNonLatin={allowNonLatin}
    />
  )
})

export { ExternalPurposeAccountDependentField, getExternalPurposeAccountDependentFields }
